import React, { useState } from 'react';
import { collection, doc, addDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import EventStepTournament from './EventSteps/EventStepTournament';
import EventStepGeneral from './EventSteps/EventStepGeneral';
import EventStepFriendly from './EventSteps/EventStepFriendly';
import EventStepLeagueMatch from './EventSteps/EventStepLeagueMatch';
import EventStepTraining from './EventSteps/EventStepTraining';
import { useAuthContext } from '../../context/AuthContext';
import {
  GeneralEventData,
  LeagueEventData,
  FriendlyEventData,
  TrainingEventData,
  TournamentEventData,
} from '../../types/EventTypes';

interface EventWizardProps {
  onSaved: () => void;
  existingEvent?: { [key: string]: any; id: string };
}

type FullEventData =
  | GeneralEventData
  | LeagueEventData
  | FriendlyEventData
  | TrainingEventData
  | TournamentEventData;

const EventWizard: React.FC<EventWizardProps> = ({
  existingEvent,
  onSaved,
}) => {
  const { user } = useAuthContext();

  const initialFormData = existingEvent
    ? (() => {
        const { id, ...data } = existingEvent;
        return { ...data };
      })()
    : {
        eventType: 'league',
      };

  const [formData, setFormData] =
    useState<Partial<FullEventData>>(initialFormData);
  const [currentStep, setCurrentStep] = useState<number>(existingEvent ? 1 : 0);

  const handleGeneralSubmit = (generalData: GeneralEventData) => {
    setFormData(prev => ({ ...prev, ...generalData }));
    setCurrentStep(1); // Move to Step 2
  };

  const handleNext = async (step2Data: Partial<FullEventData>) => {
    const mergedData = { ...formData, ...step2Data };
    setFormData(mergedData);

    if ('eventType' in mergedData) {
      console.log('Saving event with type:', mergedData.eventType);
    } else {
      console.warn('eventType is missing in mergedData', mergedData);
    }

    const path = `teams/${user?.teamId}/events`;

    try {
      if (existingEvent?.id) {
        const ref = doc(db, path, existingEvent.id);
        await updateDoc(ref, mergedData);
      } else {
        await addDoc(collection(db, path), mergedData);
      }
      onSaved();
    } catch (err) {
      console.error('Error saving event:', err);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const eventType = (formData as { eventType?: string })?.eventType;

  return (
    <div className='event-wizard'>
      {currentStep === 0 && (
        <EventStepGeneral
          initialData={formData as GeneralEventData}
          onNext={handleGeneralSubmit}
        />
      )}

      {currentStep === 1 && eventType === 'tournament' && (
        <EventStepTournament
          initialData={formData as TournamentEventData}
          onNext={handleNext}
          onBack={handleBack}
        />
      )}

      {currentStep === 1 && eventType === 'league' && (
        <EventStepLeagueMatch
          initialData={formData as LeagueEventData}
          onNext={handleNext}
          onBack={handleBack}
        />
      )}

      {currentStep === 1 && eventType === 'friendly' && (
        <EventStepFriendly
          initialData={formData as FriendlyEventData}
          onNext={handleNext}
          onBack={handleBack}
        />
      )}

      {currentStep === 1 && eventType === 'training' && (
        <EventStepTraining
          initialData={formData as TrainingEventData}
          onNext={handleNext}
          onBack={handleBack}
        />
      )}
    </div>
  );
};

export default EventWizard;
